"use client";

import React, { useState } from "react";
import { Maximize2, ChevronsLeftRight } from "lucide-react";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogTrigger, DialogTitle } from "@/components/ui/dialog";
const transformations = [{
  id: 1,
  category: "Tacos",
  title: "Al Pastor Street Tacos",
  beforeImage: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1685947/img/al_pastor_prep.png",
  afterImage: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1685947/img/al_pastor_finished.png"
}, {
  id: 2,
  category: "Tacos",
  title: "Carne Asada Supreme",
  beforeImage: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1685947/img/carne_asada_grill.png",
  afterImage: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1685947/img/carne_asada_plated.png"
}, {
  id: 3,
  category: "Sides",
  title: "Street Corn Elote",
  beforeImage: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1685947/img/corn_raw.png",
  afterImage: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1685947/img/corn_charred.png"
}, {
  id: 4,
  category: "Sides",
  title: "Loaded Nacho Box",
  beforeImage: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1685947/img/nachos_base.png",
  afterImage: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1685947/img/nachos_loaded.png"
}, {
  id: 5,
  category: "Drinks",
  title: "Fresh Mango Agua Fresca",
  beforeImage: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1685947/img/mango_fruit.png",
  afterImage: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1685947/img/mango_drink.png"
}, {
  id: 6,
  category: "Drinks",
  title: "Hibiscus Jamaica Tea",
  beforeImage: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1685947/img/hibiscus_petals.png",
  afterImage: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1685947/img/hibiscus_chilled.png"
}];
const categories = ["All", "Tacos", "Sides", "Drinks"];
export default function OurFlavorGallery() {
  return <section data-section-id="224839" id="our-flavor-gallery" className="w-full py-24 bg-background text-foreground relative overflow-hidden">
      {}
      <div className="absolute inset-0 opacity-[0.05] pointer-events-none bg-[url('https://wpvc-images.s3.us-east-1.amazonaws.com/images/1685947/img/street_texture.png')] mix-blend-overlay"></div>

      <div className="container mx-auto px-4 md:px-6 relative z-10">
        <div className="flex flex-col items-center text-center mb-16 space-y-4">
          <h2 className="font-default text-4xl md:text-7xl font-bold tracking-tighter text-foreground uppercase">
            OUR FLAVOR <span className="text-primary">GALLERY</span>
          </h2>
          <p className="text-muted-foreground max-w-2xl text-lg font-default tracking-wide">
            Slide to see the magic happen. From raw ingredients to the ultimate 
            street food experience. Visual proof of our Rolling Tacos passion.
          </p>
        </div>

        <Tabs defaultValue="All" className="w-full flex flex-col items-center">
          <TabsList className="mb-12 bg-card border-2 border-primary p-1 rounded-md">
            {categories.map((category, i) => <TabsTrigger key={category} value={category} data-index={i} className="px-6 py-2 rounded-md font-default text-sm md:text-lg font-bold uppercase transition-all data-[state=active]:bg-primary data-[state=active]:text-primary-foreground">
                {category}
              </TabsTrigger>)}
          </TabsList>

          {categories.map((category, i) => <TabsContent key={category} value={category} data-index={i} className="w-full outline-none focus-visible:ring-0">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 w-full">
                {transformations.filter(item => category === "All" || item.category === category).map((item, index) => <BeforeAfterCard key={item.id} item={item} index={index} />)}
              </div>
            </TabsContent>)}
        </Tabs>
      </div>
    </section>;
}
function BeforeAfterCard({
  item,
  index
}: {
  item: typeof transformations[0];
  index: number;
}) {
  const [sliderPosition, setSliderPosition] = useState(50);
  return <div data-index={index} className="group relative flex flex-col rounded-md overflow-hidden bg-card border-2 border-primary/30 shadow-md shadow-primary/10 transition-all duration-300 hover:border-primary hover:shadow-xl hover:shadow-primary/20 hover:scale-[1.02]">
      {}
      <Dialog>
        <DialogTrigger asChild>
          <Button variant="ghost" size="icon" className="absolute top-3 right-3 z-30 bg-background/80 hover:bg-primary text-foreground hover:text-primary-foreground backdrop-blur-md border-2 border-primary opacity-0 group-hover:opacity-100 transition-opacity duration-300 rounded-md" aria-label={`View full screen transformation of ${item.title}`}>
            <Maximize2 className="w-4 h-4" />
          </Button>
        </DialogTrigger>
        <DialogContent className="max-w-6xl w-[95vw] p-1 bg-background/95 backdrop-blur-xl border-2 border-primary shadow-4xl">
          <DialogTitle className="sr-only">
            {item.title} Transformation
          </DialogTitle>
          <div className="relative w-full aspect-video rounded-md overflow-hidden">
            <InteractiveSlider beforeImage={item.beforeImage} afterImage={item.afterImage} title={item.title} initialPosition={sliderPosition} />
          </div>
        </DialogContent>
      </Dialog>

      {}
      <div className="relative aspect-[4/3] w-full overflow-hidden">
        <InteractiveSlider beforeImage={item.beforeImage} afterImage={item.afterImage} title={item.title} initialPosition={50} onChange={setSliderPosition} />
      </div>

      {}
      <div className="p-5 border-t-2 border-primary/20 bg-gradient-to-b from-transparent to-primary/5">
        <div className="flex items-center justify-between mb-2">
          <span className="text-xs font-mono tracking-widest text-primary-foreground uppercase px-2 py-1 bg-primary rounded-md font-bold">
            {item.category}
          </span>
        </div>
        <h3 className="font-default text-2xl font-bold text-foreground leading-tight uppercase tracking-tight">
          {item.title}
        </h3>
      </div>
    </div>;
}
function InteractiveSlider({
  beforeImage,
  afterImage,
  title,
  initialPosition = 50,
  onChange
}: {
  beforeImage: string;
  afterImage: string;
  title: string;
  initialPosition?: number;
  onChange?: (pos: number) => void;
}) {
  const [position, setPosition] = useState(initialPosition);
  const handleSliderChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newPos = Number(e.target.value);
    setPosition(newPos);
    if (onChange) onChange(newPos);
  };
  return <div className="relative w-full h-full select-none group/slider">
      {}
      <img src={afterImage} alt={`${title} - After`} className="absolute inset-0 w-full h-full object-cover" loading="lazy" />

      {}
      <img src={beforeImage} alt={`${title} - Before`} className="absolute inset-0 w-full h-full object-cover" style={{
      clipPath: `polygon(0 0, ${position}% 0, ${position}% 100%, 0 100%)`
    }} loading="lazy" />

      {}
      <div className="absolute top-0 bottom-0 w-1 bg-primary z-10 pointer-events-none transition-opacity duration-300" style={{
      left: `${position}%`
    }}>
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-10 h-10 bg-primary border-2 border-background rounded-md flex items-center justify-center shadow-lg text-primary-foreground">
          <ChevronsLeftRight className="w-6 h-6" />
        </div>
      </div>

      {}
      <div className="absolute bottom-4 left-4 z-10 pointer-events-none opacity-0 group-hover/slider:opacity-100 transition-opacity duration-300">
        <span className="bg-background/90 backdrop-blur-sm text-foreground text-xs font-bold uppercase tracking-widest px-3 py-1 rounded-md shadow-md border-2 border-primary">
          Prep
        </span>
      </div>
      <div className="absolute bottom-4 right-4 z-10 pointer-events-none opacity-0 group-hover/slider:opacity-100 transition-opacity duration-300">
        <span className="bg-primary text-primary-foreground text-xs font-bold uppercase tracking-widest px-3 py-1 rounded-md shadow-md">
          Served
        </span>
      </div>

      {}
      <input type="range" min="0" max="100" value={position} onChange={handleSliderChange} className="absolute inset-0 w-full h-full opacity-0 cursor-ew-resize z-20" aria-label={`Compare prep and served for ${title}`} />
    </div>;
}